import { useMemo } from 'react';
import { ColDef, FirstDataRenderedEvent, ICellRendererParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { IHeaderParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { AgGridReact } from 'ag-grid-react';
import { IDashPwlLabBilling } from '@nkr/models/dash-pwl-labbilling';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import { Glyphicon } from 'react-bootstrap';
import { BarChart } from 'recharts';

const rowClassRules = {
    'custom-row-border': () => true, // Apply the custom-row-border class to all rows
};

interface IProps {
    centerId: number,
    labData: IDashPwlLabBilling[],
    isAdmin: boolean
}

const Grid = (props: IProps) => {
    const RowIndexRenderer = (props) => {
        const cellStyle = {
            paddingTop: '3px',
        };

        return (
            <div className="row-index-cell" style={cellStyle}>
                {props.node.rowIndex + 1}
            </div>
        );
    }

    const frameworkComponents = {
        rowIndexRenderer: RowIndexRenderer,
    };

    const cols: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'Rank',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Rank</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            width: 85
        },
        {
            colId: 'entrySource',
            field: 'entrySource',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Source</SortableHeaderComponent>,
            cellStyle: { textAlign: 'center' }
        },
        {
            colId: 'organType',
            field: 'organType',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
            cellStyle: { textAlign: 'center' }
        },
        {
            colId: 'registrationFullName',
            field: 'registrationFullName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let { registrationId, registrationFullName, centerId } = params.data;
                if (!centerId || centerId === 0) return <div>{registrationFullName}</div>;
                return <div><a href={`/center/${centerId}/donors/donor-intake/details/${registrationId}`}>{registrationFullName}</a> </div>
            },
            cellStyle: { textAlign: 'center' }
        },
        {
            colId: 'registrationDob',
            field: 'registrationDob',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Dob</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let { registrationDob } = params.data;

                return <div>{formatDateToISO(registrationDob)}</div>
            },
            cellStyle: { textAlign: 'center' },
            comparator: Utility.dateComparator
        },
        {
            colId: 'uploadDateTime',
            field: 'uploadDateTime',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Uploaded</SortableHeaderComponent>,
            cellStyle: { textAlign: 'center' },
            comparator: Utility.dateComparator
        },
        {
            colId: 'isBillable',
            field: 'isBillable',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Billable?</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let { isBillable } = params.data;

                if (isBillable) return <span className='label label-success'>yes</span>
                if (!isBillable) return <span className='label label-default'>no</span>
                return ""
            },
            cellStyle: { textAlign: 'center' }
        },
        {
            colId: 'phlebot',
            field: 'phlebot',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Phlebot</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let { phlebot } = params.data;

                if (phlebot) return <Glyphicon glyph='ok' />;
                return ""
            },
            cellStyle: { textAlign: 'center' }
        },
        {
            colId: '',
            field: '',
            headerName: 'Actions',
            cellRendererFramework: (params: ICellRendererParams) => {
                let { registrationId, centerId } = params.data;

                if (registrationId === null || centerId === null || centerId === 0) 
                    return ''
                if (registrationId === null || centerId === null || centerId === 0) 
                    return ''
                return <a href={`/center/${centerId}/donors/donor-intake/pwl/details/${registrationId}`}><button className='btn btn-primary btn-xs' >View PWL Log </button></a>
            },
            cellStyle: { textAlign: 'center' }
        }

    ];

    const formatDateToISO = (dateString: string) => {
        if (dateString == null) return '';
        if (dateString == '') return '';

        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Adding 1 to month since it's zero-based
        const day = String(date.getDate()).padStart(2, '0');

        return `${month}/${day}/${year}`;
    };

    // set background colour on even rows again, this looks bad, should be using CSS classes
    const getRowStyle = params => {
        if (params.node.rowIndex % 2 === 0) {
            return { background: 'rgb(190,190,190, 0.3)' };
        }
    };

    const columnDefs = useMemo(() => cols, [])
    const rowData = useMemo(() => props.labData, [props.labData]);

    return (
        <GridWrapper>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: true
                }}
                enableRangeSelection={true}
                columnDefs={columnDefs}
                rowData={rowData}
                getRowStyle={getRowStyle}
                headerHeight={30}
                suppressCellSelection={true}
                suppressContextMenu={true}
                rowClassRules={rowClassRules}
                frameworkComponents={frameworkComponents}
                noRowsOverlayComponentFramework={() =>
                    <div>No data for the selected parameters...</div>}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
            />
        </GridWrapper>
    )

}


export default Grid;